// src/components/InitialPasswordReminderModal.js
// Temporary-password reminder plus mandatory first-login change for bulk chef accounts.
// Firestore reminder flag is dismissible; initialPasswordChangeRequired is not.

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword,
} from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../services/firebase';
import { userHasAdminRole, userHasProductionRole } from '../services/adminDesktopNotificationService';
import './InitialPasswordReminderModal.css';

function sessionSuppressKey(uid) {
  return `lsc_initial_pwd_reminder_suppress_${uid}`;
}

function resolvePendingFlag(data) {
  return data && data.initialPasswordReminderPending === true;
}

function resolveRequiredFlag(data) {
  return data && data.initialPasswordChangeRequired === true;
}

export default function InitialPasswordReminderModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [required, setRequired] = useState(false);
  const [sessionHidden, setSessionHidden] = useState(false);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staffSession, setStaffSession] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeError, setChangeError] = useState('');

  useEffect(() => {
    if (!user?.uid) {
      setPending(false);
      setRequired(false);
      setSessionHidden(false);
      setStaffSession(false);
      return undefined;
    }
    try {
      setSessionHidden(sessionStorage.getItem(sessionSuppressKey(user.uid)) === '1');
    } catch {
      setSessionHidden(false);
    }
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setPending(resolvePendingFlag(data));
      setRequired(resolveRequiredFlag(data));
      setStaffSession(userHasAdminRole(data) || userHasProductionRole(data));
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if ((!pending && !required) || (!required && (sessionHidden || staffSession))) {
      setVisible(false);
      return undefined;
    }
    const t = setTimeout(() => setVisible(true), required ? 50 : 450);
    return () => clearTimeout(t);
  }, [pending, required, sessionHidden, staffSession]);

  const clearReminder = useCallback(async () => {
    if (!user?.uid) return;
    try {
      sessionStorage.removeItem(sessionSuppressKey(user.uid));
    } catch {
      /* ignore */
    }
    await updateDoc(doc(db, 'users', user.uid), {
      initialPasswordReminderPending: false,
      initialPasswordReminderResolvedAt: new Date().toISOString(),
    });
  }, [user?.uid]);

  const handleSendReset = async () => {
    if (!user?.email || busy) return;
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      await clearReminder();
      setVisible(false);
      alert('Check your inbox for a link to set a new password.');
    } catch (e) {
      console.error('[InitialPasswordReminderModal] sendPasswordResetEmail', e);
      alert('Could not send the reset email. Try again from My account.');
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await clearReminder();
      setVisible(false);
    } catch (e) {
      console.error('[InitialPasswordReminderModal] dismiss', e);
    } finally {
      setBusy(false);
    }
  };

  const handleGoAccount = () => {
    if (!user?.uid) return;
    try {
      sessionStorage.setItem(sessionSuppressKey(user.uid), '1');
    } catch {
      /* ignore */
    }
    setSessionHidden(true);
    setVisible(false);
    navigate('/account');
  };

  const handleRequiredPasswordChange = async (event) => {
    event.preventDefault();
    if (!user?.uid || !user?.email || busy) return;
    setChangeError('');
    if (!currentPassword) {
      setChangeError('Enter the temporary password used to sign in.');
      return;
    }
    if (newPassword.length < 10) {
      setChangeError('Your new password must be at least 10 characters.');
      return;
    }
    if (newPassword === currentPassword) {
      setChangeError('Choose a new password that is different from the temporary password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangeError('The new password confirmation does not match.');
      return;
    }

    setBusy(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      await updateDoc(doc(db, 'users', user.uid), {
        initialPasswordChangeRequired: false,
        initialPasswordChangedAt: new Date().toISOString(),
        initialPasswordReminderPending: false,
        initialPasswordReminderResolvedAt: new Date().toISOString(),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setVisible(false);
    } catch (error) {
      console.error('[InitialPasswordReminderModal] required password change', error);
      const code = String(error?.code || '');
      if (code.includes('invalid-credential') || code.includes('wrong-password')) {
        setChangeError('The temporary password is incorrect.');
      } else if (code.includes('weak-password')) {
        setChangeError(error?.message || 'Firebase rejected the new password as too weak.');
      } else {
        setChangeError('The password could not be changed. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (
    !user?.uid ||
    (!pending && !required) ||
    (!required && (sessionHidden || staffSession))
  ) {
    return null;
  }

  return (
    <div
      className={`ipr-overlay ${visible ? 'ipr-visible' : ''}`}
      role="presentation"
      data-scan-suspend="true"
    >
      <div
        className="ipr-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ipr-title"
        onClick={(e) => e.stopPropagation()}
      >
        {required ? (
          <>
            <h2 id="ipr-title" className="ipr-title">
              Choose your private password
            </h2>
            <p id="ipr-required-help" className="ipr-body">
              Your London Smoke &amp; Cure wholesale account has a temporary password. You must replace it
              before continuing to the site. This change does not send an email.
            </p>
            <form
              className="ipr-password-form"
              onSubmit={handleRequiredPasswordChange}
              aria-describedby="ipr-required-help"
            >
              <label htmlFor="ipr-current-password">Temporary password</label>
              <input
                id="ipr-current-password"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                disabled={busy}
                autoFocus
              />
              <label htmlFor="ipr-new-password">New password</label>
              <input
                id="ipr-new-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                disabled={busy}
              />
              <label htmlFor="ipr-confirm-password">Confirm new password</label>
              <input
                id="ipr-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                disabled={busy}
              />
              {changeError ? (
                <p className="ipr-form-error" role="alert">
                  {changeError}
                </p>
              ) : null}
              <button type="submit" className="ipr-btn ipr-btn-primary" disabled={busy}>
                {busy ? 'Changing password...' : 'Set new password and continue'}
              </button>
            </form>
            <p className="ipr-footnote">
              Use at least 10 characters. The new password must differ from the temporary password.
            </p>
          </>
        ) : (
          <>
            <h2 id="ipr-title" className="ipr-title">
              Set a stronger password
            </h2>
            <p className="ipr-body">
              Your London Smoke &amp; Cure wholesale account was set up with a temporary password. For security,
              please choose a new password you have not used elsewhere.
            </p>
            <div className="ipr-actions">
              <button type="button" className="ipr-btn ipr-btn-primary" disabled={busy} onClick={handleSendReset}>
                Email me a reset link
              </button>
              <button type="button" className="ipr-btn ipr-btn-secondary" disabled={busy} onClick={handleGoAccount}>
                My account
              </button>
              <button type="button" className="ipr-btn ipr-btn-ghost" disabled={busy} onClick={handleDismiss}>
                Dismiss
              </button>
            </div>
            <p className="ipr-footnote">
              You can change your password any time under My account (Reset password). This message is shown once.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
